export const createRequestRefreshScheduler = ({
    request,
    applyResult,
    onError,
    delay = 1000,
    debounce = true,
}) => {
    let timeoutId;
    let inFlight = false;
    let trailingRefresh = false;
    let disposed = false;
    let callbacks = [];

    const finishCallbacks = outcome => {
        const pendingCallbacks = callbacks;
        callbacks = [];
        pendingCallbacks.forEach(callback => callback(outcome));
    };

    const scheduleRequest = () => {
        if (!debounce && timeoutId) return;

        clearTimeout(timeoutId);
        timeoutId = setTimeout(run, delay);
    };

    const run = async () => {
        timeoutId = undefined;
        inFlight = true;
        let outcome;

        try {
            const result = await request();
            if (!disposed) applyResult?.(result);
            outcome = {ok: true, result};
        } catch (error) {
            if (!disposed) onError?.(error);
            outcome = {ok: false, error};
        } finally {
            inFlight = false;
            if (disposed) return;

            if (trailingRefresh) {
                trailingRefresh = false;
                scheduleRequest();
            } else {
                finishCallbacks(outcome);
            }
        }
    };

    return {
        schedule(callback) {
            if (disposed) {
                callback?.({ok: false, disposed: true});
                return;
            }
            if (callback) callbacks.push(callback);

            if (inFlight) {
                trailingRefresh = true;
            } else {
                scheduleRequest();
            }
        },

        schedulePromise() {
            return new Promise((resolve, reject) => {
                this.schedule(outcome => {
                    if (outcome?.disposed) {
                        resolve({disposed: true});
                    } else if (outcome?.ok === false) {
                        reject(outcome.error || new Error('Refresh was canceled'));
                    } else {
                        resolve(outcome?.result);
                    }
                });
            });
        },

        dispose() {
            disposed = true;
            clearTimeout(timeoutId);
            finishCallbacks({ok: false, disposed: true});
        },
    };
};

export const createLatestRequestRunner = () => {
    let generation = 0;

    return {
        run(request, {applyResult, onError, rejectOnError = false} = {}) {
            const requestGeneration = ++generation;
            return Promise.resolve()
                .then(request)
                .then(
                    result => {
                        if (requestGeneration === generation) applyResult?.(result);
                        return result;
                    },
                    error => {
                        if (requestGeneration === generation) onError?.(error);
                        if (rejectOnError) throw error;
                        return undefined;
                    },
                );
        },

        invalidate(onInvalidate) {
            generation += 1;
            onInvalidate?.();
        },
    };
};
